import React, { useCallback, useState } from 'react'
import { useRouter } from 'next/router'

import {
  useGetMaterialByModule,
  useGetModulePreview,
} from 'api/microlearning/useAPIModule'
import { useModuleExpanderStore } from 'stores/microlearning'
import { GATrackEvent } from 'utils/googleAnalytics'
import ModuleDetailsDesktop from './ModuleDetails.desktop'
import ModuleDetailsMobile from './ModuleDetails.mobile'

type ModuleDetailsProps = {
  desktopContent?: React.ReactNode
  displayMode?: 'page' | 'panel'
  isDesktop?: boolean
  returnView?: 'topic'
}

const ModuleDetails = ({
  desktopContent,
  displayMode = 'page',
  isDesktop = false,
  returnView,
}: ModuleDetailsProps) => {
  const { query } = useRouter()
  const modulePreview = useGetModulePreview(query.modul as string)
  const {
    isLoading: materialDataIsLoading,
    isSuccess: materialDataIsSuccess,
    data: materialData,
  } = useGetMaterialByModule(query.modul as string)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const { lastExpandedIds, setLastExpandedIds } = useModuleExpanderStore()

  const isLoading = modulePreview.isLoading || materialDataIsLoading
  const isSuccess = modulePreview.isSuccess && materialDataIsSuccess

  React.useEffect(() => {
    if (query.materi) {
      const referredMateri = `${query.topik}_${query.modul}_${query.materi}`
      if (!lastExpandedIds.includes(referredMateri)) {
        setLastExpandedIds([...lastExpandedIds, referredMateri])
      }
    }
  }, [lastExpandedIds, setLastExpandedIds, query])

  const toggleShowDetail = useCallback(() => {
    setIsDrawerOpen((isShown) => {
      if (!isShown) {
        GATrackEvent({
          eventName: 'learning_activity',
          eventParams: {
            event_trigger: 'click_view_module_detail',
            action_type: 'view_module_detail',
            meta: JSON.stringify({
              module_id: query.modul,
            }),
          },
          sendDefaultParams: true,
        })
      }

      return !isShown
    })
  }, [query.modul])

  const toggleMateriExpand = useCallback(
    (currentId: string) => {
      if (lastExpandedIds.includes(currentId)) {
        return setLastExpandedIds(
          lastExpandedIds.filter((id) => id !== currentId)
        )
      }

      return setLastExpandedIds([...lastExpandedIds, currentId])
    },
    [lastExpandedIds, setLastExpandedIds]
  )

  if (isDesktop) {
    return (
      <ModuleDetailsDesktop
        modulePreview={modulePreview?.data}
        materialData={materialData}
        isLoading={isLoading}
        isSuccess={isSuccess}
        toggleShowDetail={toggleShowDetail}
        toggleMateriExpand={toggleMateriExpand}
        isDrawerOpen={isDrawerOpen}
        onDialogClose={() => setIsDrawerOpen(false)}
        lastExpandedIds={lastExpandedIds}
        content={desktopContent}
        displayMode={displayMode}
        returnView={returnView}
      />
    )
  }

  return (
    <ModuleDetailsMobile
      modulePreview={modulePreview?.data}
      isLoading={isLoading}
      isSuccess={isSuccess}
      materialData={materialData}
      toggleShowDetail={toggleShowDetail}
      toggleMateriExpand={toggleMateriExpand}
      isDrawerOpen={isDrawerOpen}
      setIsDrawerOpen={setIsDrawerOpen}
      lastExpandedIds={lastExpandedIds}
    />
  )
}

export default ModuleDetails
